// Сид дня: один стол на всех (сид, мутатор и характеры трёх сложных ботов выдаёт сервер — GET api/daily), одна
// попытка в день на аккаунт (POST api/daily/start регистрирует её, итог уходит обычной записью партии с полем
// daily — см. outbox.ts), таблица дня, вчерашняя и вечный зал славы. Экран — DailyHome в screens.ts, состояние —
// st.daily; на экране итога партии дня — место в таблице (DailyResultV).
import type { DailyInfo, DailySpec, PlayedGame, PlayerProfile } from '../types.ts';
import { clientId, deviceKey, flushOutbox } from './outbox.ts';
import { st, ui, toast } from './store.ts';
import { loadGame } from './storage.ts';
import { startNewGame } from './flow.ts';
import { render } from './render.ts';
import { startReplay } from './replay.ts';

function isDailyInfo(x: unknown): x is DailyInfo {
  if (!x || typeof x !== 'object') return false;
  const o = x as Partial<DailyInfo>;
  return !!o.spec && typeof o.spec.day === 'string' && typeof o.spec.seed === 'number' && Array.isArray(o.spec.bots) && !!o.today && Array.isArray(o.today.rows) && Array.isArray(o.fame) && !!o.me;
}

/** Загрузить экран сида дня; repaint — перерисовать, когда ответ пришёл (или не пришёл) */
export async function loadDaily(repaint: () => void): Promise<void> {
  if (st.daily.loading) return;
  st.daily.loading = true;
  repaint();
  try {
    // сначала — неотправленные партии этого устройства: только что сыгранная попытка должна попасть в таблицу
    await flushOutbox();
    const r = await fetch(`api/daily?client=${encodeURIComponent(clientId())}`);
    const j: unknown = r.ok ? await r.json() : null;
    if (!isDailyInfo(j)) throw new Error('bad daily');
    st.daily.data = j;
    st.daily.error = false;
  } catch {
    st.daily.error = true;
  } finally {
    st.daily.loading = false;
    repaint();
  }
}

/** Незаконченная попытка дня на этом устройстве (сохранённая партия с меткой дня) */
export function localDailySave(day: string): boolean {
  const s = loadGame();
  return !!s && s.G.daily === day && s.G.phase !== 'ended';
}

/** Начать попытку дня: сервер регистрирует её (одна в день) и отдаёт стол; затем обычная хотсит-партия с этим столом */
export async function startDaily(): Promise<void> {
  if (st.daily.starting) return;
  st.daily.starting = true;
  st.daily.startErr = '';
  render();
  try {
    const r = await fetch('api/daily/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client: clientId(), key: deviceKey(), name: ui.onlineName }),
    });
    const j = (await r.json().catch(() => null)) as { ok?: boolean; spec?: DailySpec; error?: string } | null;
    if (!r.ok || !j?.ok || !j.spec) throw new Error(j?.error ?? `ошибка сервера (${r.status})`);
    const spec = j.spec;
    const bots = spec.bots.map((b) => b.level);
    startNewGame(
      { n: bots.length + 1, bots: [null, ...bots], names: [ui.onlineName || 'Ты'] },
      { seed: spec.seed, mutators: spec.mutators, styles: [null, ...spec.bots.map((b) => b.style)], bosses: [null, ...spec.bots.map((b) => b.boss ?? null)], daily: spec.day },
    );
    // таблица дня устарела: моя попытка начата
    if (st.daily.data) st.daily.data.me = { status: 'playing', score: 0, margin: 0, won: false };
  } catch (e) {
    st.daily.startErr = e instanceof TypeError ? 'Нет связи с сервером — сид дня играется только онлайн.' : e instanceof Error ? e.message : String(e);
    toast('⚠ ' + st.daily.startErr, 4000);
    void loadDaily(render);
  } finally {
    st.daily.starting = false;
    render();
  }
}

/** После партии дня: отправить запись и обновить таблицу — место на экране итога */
let resultFor: string | null = null;
export async function loadDailyResult(day: string, repaint: () => void): Promise<void> {
  if (resultFor === day) return;
  resultFor = day;
  await loadDaily(repaint);
}

/** Партия дня для просмотра в плеере (прошедший день): запись открывается как партия из рейтингов */
export function showDailyGame(game: number, name: string, score: number, won: boolean): void {
  const g: PlayedGame = { game, at: 0, cat: 'bots', level: 'hard', place: won ? 0 : 1, players: 4, score, seat: 0, table: [] };
  void startReplay(g, name, false);
}

/** Мои встречи с боссами — из своего профиля (экран дуэли и плитка на хабе); без сети — экран без отметок */
export async function loadMyBosses(repaint: () => void): Promise<void> {
  if (st.bosses.loading) return;
  st.bosses.loading = true;
  try {
    await flushOutbox();
    const r = await fetch(`api/player?client=${encodeURIComponent(clientId())}`);
    const j = (r.ok ? await r.json() : null) as Partial<PlayerProfile> | null;
    st.bosses.data = Array.isArray(j?.bosses) ? j.bosses : [];
    st.bosses.error = false;
  } catch {
    st.bosses.error = true;
  } finally {
    st.bosses.loading = false;
    repaint();
  }
}
